/**
 * Sentry error monitoring — initializes only in production builds.
 *
 * In local dev (`import.meta.env.PROD === false`) or when VITE_SENTRY_DSN
 * is not set, this is a no-op so no errors or traces are sent.
 *
 * Production setup: set VITE_SENTRY_DSN in your hosting environment
 * (e.g. AWS Amplify environment variables). Never commit the real DSN
 * to .env.local or source control.
 */

import * as Sentry from '@sentry/react';
import type { RootOptions } from 'react-dom/client';

function isSentryEnabled(): boolean {
  return import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN);
}

/**
 * React 19 reports render errors through `createRoot` callbacks rather than rethrowing them,
 * so Sentry needs these to see them. Returns `{}` when Sentry is off, which keeps React's
 * default console logging in development.
 */
export function sentryRootErrorHandlers(): RootOptions {
  if (!isSentryEnabled()) return {};
  return {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  };
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!isSentryEnabled() || !dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: 'production',
    integrations: [Sentry.browserTracingIntegration()],
    /**
     * Capture 10% of transactions for performance monitoring.
     * Increase to 1.0 temporarily when diagnosing a performance issue.
     */
    tracesSampleRate: 0.1,
    /**
     * Keep false — this app handles family and minor-child data.
     * Sentry will not automatically attach IP addresses or user agents.
     */
    sendDefaultPii: false,
  });
}

export { Sentry };
